//codex-- Page-scoped toolbar layout; retain this project's handlers and data.
import PageToolbar, { ToolbarField, ToolbarFooterAction } from './PageToolbar';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import DateFormat from '../components/DateFormat';
import SubMenu from '../components/SubMenu'; // Assuming you have a SubMenu component
import EditPOModal from '../components/EditPOModal'; // create this as needed
import { useNavigate } from 'react-router-dom';

const POListPage = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [POs, setPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editPO, setEditPO] = useState(null);

  useEffect(() => {
    fetchPOs();
  }, []);

  const getLinksForJob = (po) => {
    const links = [
      {
        name: 'Edit PO',
        click: () => {
          setEditPO(po);       // store the PO object to state
          setModalOpen(true);  // open the modal
        }
      }
    ];
    // You can add more links as needed
    return links;
  };
  
  const fetchPOs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/inventory_crud.php?purchase_orders=1`);
      if (Array.isArray(res.data) && res.data[0] === 'success') {
        setPOs(res.data[1]);
      } 
    } catch {
      toast.error('API error');
    } finally {
      setLoading(false);
    }
  };

  // Filtering logic
  const filteredPOs = POs.filter(po =>
    (!search || po.po_number.toLowerCase().includes(search.toLowerCase()) || po.vendor.toLowerCase().includes(search.toLowerCase()))
    && (!statusFilter || po.status === statusFilter)
  );

  const handleGeneratePO = async () => {
    // if (!window.confirm("Generate PO for all inventory below reorder level?")) return;
    try {
      const res = await axios.post(`${apiUrl}/inventory_crud.php`, {
        status: 'generatePO',
        // Optionally: you can pass user info, vendor, etc.
      });
      if (res.data.success) {
        toast.success(res.data.success);
        fetchPOs(); // Refresh the PO list
      } else if (res.data.error) {
        toast.error(res.data.error);
      } else {
        toast.error('No PO generated');
      }
    } catch (e) {
      toast.error('Error generating PO');
    }
  };

  const handleSavePO = async (updatedPO) => {
      const isNew = !updatedPO.id;
      const bodyData = {
        data: updatedPO,
        status: isNew ? 'addPO' : 'updatePO',
      }
    try {
      const res = await axios.post(`${apiUrl}/inventory_crud.php`,  bodyData);
      if (res.data.success) {
        toast.success(isNew ? 'PO created!' : 'PO updated!');
        setModalOpen(false);
        fetchPOs();
      } else {
        toast.error(res.data.error || (isNew ? 'Failed to create PO' : 'Failed to update PO'));
      }
    } catch (e) {
      if (e.response && e.response.status === 401) {
        toast.error('Session expired, please log in again', { position: 'top-center' });
        navigate('/login');
      } else if (e.response && e.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", { position: 'top-center' });
      } else {
        console.error(e);
        toast.error('Error saving PO', { position: 'top-center' });
      }
      setModalOpen(false);
      setEditPO(null);
    }
  };

  
  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-xl font-bold mb-4">Inventory Purchase Orders</h1>
    
      {/* Filter Bar */}
      <>{/* //codex-- Match this page's toolbar to its asmainframe reference. */}<PageToolbar title="Purchase order tools" actions={<>
          <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded" onClick={() => {
            setModalOpen(true);
            setEditPO({});
          }}>+ New PO</button>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded" onClick={handleGeneratePO}>Generate PO</button>
        </>}>
          <ToolbarField label="Search"><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search PO number or Vendor" className="w-full md:w-2/5 border border-gray-300 rounded-md px-3 py-2" /></ToolbarField>
          <ToolbarField label="Status"><select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2 w-full md:w-auto">
              <option value="">All Status</option>
              <option value="Open">Open</option>
              <option value="Ordered">Ordered</option>
              <option value="Received">Received</option>
              <option value="Closed">Closed</option>
              <option value="Cancelled">Cancelled</option>
            </select></ToolbarField>
          <ToolbarFooterAction><button onClick={() => {
            setSearch('');
            setStatusFilter('');
          }} className="bg-orange-500 hover:bg-orange-700 text-white px-4 py-2 rounded">Clear</button></ToolbarFooterAction>
        </PageToolbar></>

      {/* Table */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="">
          <table className="min-w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-200">
              <tr>
                <th className="border px-4 py-2">ID | PO#</th>
                <th className="border px-4 py-2">Created By</th>
                <th className="border px-4 py-2">Vendor</th>
                <th className="border px-4 py-2">Job Number</th>
                <th className="border px-4 py-2">Created | Expected</th>
                <th className="border px-4 py-2">Status</th>
                <th className="border px-4 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filteredPOs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-6 text-gray-400">No Purchase Orders Found.</td>
                </tr>
              ) : (
                filteredPOs.map(po => (
                  <tr
                      key={po.id}
                      className={`
                        hover:bg-indigo-50 hover:cursor-pointer
                        ${po.status === 'Ordered' ? 'bg-yellow-100' : ''}
                        ${po.status === 'Cancelled' ? 'bg-red-100 text-red-800' : ''}
                        ${po.status === 'Closed' ? 'bg-green-100 text-green-800' : ''}
                      `}
                      onClick={() => navigate(`/po/${po.id}`)}
                    >
                    <td className="border px-3 py-2 text-sm">
                      <SubMenu label={po.id} idItem={po.id} links={getLinksForJob(po.id)} />
                      <div className="text-gray-400 text-xs">| {po.po_number}</div>
                    </td>
                    <td className="border px-3 py-2 text-sm">{po.created_by ?? '-'}</td>
                    <td className="border px-3 py-2 text-sm">{po.vendor}</td>
                    <td className="border px-3 py-2 text-sm">{po.job_number}</td>
                    <td className="border px-3 py-2 text-sm">
                      <DateFormat date={po.date_created} />
                      <div className="text-gray-500 text-xs">| <DateFormat date={po.expected_date} /></div>
                    </td>
                    <td className="border px-3 py-2 text-sm">
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-semibold
                        ${po.status === 'Ordered'   ? 'bg-yellow-200 text-yellow-800' : ''}
                        ${po.status === 'Cancelled' ? 'bg-red-200 text-red-800' : ''}
                        ${po.status === 'Closed'    ? 'bg-green-200 text-green-800' : ''}
                      ${po.status === 'Open'      ? 'bg-gray-200 text-gray-700' : ''}
                    `}>
                      {po.status}
                    </span>
                  </td>
                    <td className="border px-3 py-2 text-sm max-w-xs truncate">{po.notes}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && editPO && (
        <EditPOModal
          po={editPO}
          onClose={() => setModalOpen(false)}
          onSave={handleSavePO}
        />
      )}

    </div>
  );
};

export default POListPage;
